import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DiscoveredPage } from '../types'
import { serverURL } from '../api/config'
import { normalizeUrl } from '../utils/normalizeUrl'

async function discoverPages(
  token: string,
  orgUuid: string,
  website: string
): Promise<DiscoveredPage[]> {
  // Ensure protocol so the backend can navigate via Playwright
  const normalizedWebsite = website.startsWith('http') ? website : `https://${website}`
  const response = await fetch(
    `${serverURL}/orgs/${orgUuid}/discover-pages`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ website: normalizedWebsite }),
    }
  )
  // Thrown, not returned as []: an empty list means "the site has no other
  // pages", and callers cache it as such.
  if (!response.ok) throw new Error(`Page discovery failed: ${response.status}`)
  const data = await response.json()
  return data.pages ?? []
}

/**
 * Page discovery, one request per website at a time.
 *
 * Leaving the website field, continuing from Org details and opening the
 * webpages step can all ask for the same site within seconds. Each request
 * runs a headless browser, so a call made while another is in flight for the
 * same site waits on that one instead of starting its own. Results are not
 * kept here: the wizard caches successes itself, and a failure must stay
 * retryable.
 */
export function useDiscoverPages() {
  const queryClient = useQueryClient()
  const discover = useCallback(
    ({
      token,
      orgUuid,
      website,
    }: {
      token: string
      orgUuid: string
      website: string
    }) =>
      queryClient.fetchQuery({
        queryKey: ['discoverPages', orgUuid, normalizeUrl(website)],
        queryFn: () => discoverPages(token, orgUuid, website),
        staleTime: 0,
        gcTime: 0,
        retry: false,
      }),
    [queryClient]
  )
  return { discover }
}
